import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { HeadService } from '../../../../services/head.service';
import { CustomerService } from '../../../../services/customer.service';
import type { CartItemDto, OrderDto } from '@api/models';
import { OrderStatusEnum } from '@api/models';
import { finalize } from 'rxjs/operators';
import { LanguageService } from '../../../../services/language/language.service';
import { DatePipe } from '@angular/common';
import { AccountOrdersSkeletonComponent } from '@/pages/account-page/components/account-orders-skeleton/account-orders-skeleton.component';
import { TranslatePipe } from '@ngx-translate/core';
import { resolveHttpErrorMessage } from '@/helpers/resolve-http-error-message.helper';
import { CartModalComponent, CartModalData } from '@/components/cart-modal/cart-modal.component';
import { ButtonComponent } from '@/components/button/button.component';
import { IconComponent } from '@/components/icon/icon.component';
import { NotyService } from '@/components/noty/noty.service';
import { OrderPaymentOptionsComponent } from '@/components/order-payment-options/order-payment-options.component';
import { OrderDetailsComponent } from '@/components/order-details/order-details.component';
import { CartService } from '@/services/cart.service';

export const ORDERS_PAGE_SIZE = 20;
export const PREVIEW_IMAGES_COUNT = 4;

const DANGER_STATUSES: OrderStatusEnum[] = [
  OrderStatusEnum.CANCELED,
  OrderStatusEnum.RECIPIENT_DENIED,
  OrderStatusEnum.RETURNING,
  OrderStatusEnum.RETURNED,
  OrderStatusEnum.REFUSED_TO_RETURN,
];

@Component({
  selector: 'account-orders',
  templateUrl: './account-orders.component.html',
  styleUrls: ['./account-orders.component.scss'],
  imports: [
    AccountOrdersSkeletonComponent,
    DatePipe,
    TranslatePipe,
    OrderDetailsComponent,
    ButtonComponent,
    IconComponent,
    OrderPaymentOptionsComponent,
  ],
  host: { '[attr.aria-busy]': 'isLoading()' },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountOrdersComponent implements OnInit {
  private headService = inject(HeadService);
  private customerService = inject(CustomerService);
  private cartService = inject(CartService);
  private dialog = inject(MatDialog);
  private notyService = inject(NotyService);
  private destroyRef = inject(DestroyRef);
  protected languageService = inject(LanguageService);

  orders = signal<OrderDto[]>([]);
  error = signal<string | undefined>(undefined);
  isLoading = signal(false);
  expandedOrderIds = signal<Set<string>>(new Set(), { equal: (_a, _b) => false });
  searchQuery = signal('');
  searchInput = signal('');
  page = signal(1);
  reorderingOrderId = signal<string | undefined>(undefined);
  readonly previewImagesCount = PREVIEW_IMAGES_COUNT;

  /** Orders with at least one product whose name contains the search text */
  filteredOrders = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    if (!query) {
      return this.orders();
    }
    return this.orders().filter(order => order.items.some(item => item.name.toLowerCase().includes(query)));
  });
  pagesCount = computed(() => Math.max(1, Math.ceil(this.filteredOrders().length / ORDERS_PAGE_SIZE)));
  pageNumbers = computed(() => Array.from({ length: this.pagesCount() }, (_, i) => i + 1));
  pageOrders = computed(() => {
    const start = (this.page() - 1) * ORDERS_PAGE_SIZE;
    return this.filteredOrders().slice(start, start + ORDERS_PAGE_SIZE);
  });

  ngOnInit(): void {
    this.fetchOrders();
    this.setMeta();
  }

  fetchOrders(): void {
    this.isLoading.set(true);
    this.error.set(undefined);
    this.customerService.fetchOrders().pipe(
      finalize(() => this.isLoading.set(false)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: response => this.orders.set(response.data),
      error: err => this.error.set(resolveHttpErrorMessage(err))
    });
  }

  private setMeta(): void {
    this.languageService.getTranslation('global.my_orders').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: text => {
        this.headService.setMeta({ title: text, description: text });
      }
    });
  }

  statusTone(order: OrderDto): 'danger' | 'success' | 'warning' | 'neutral' {
    if (DANGER_STATUSES.includes(order.statusId)) {
      return 'danger';
    }
    if (order.statusId === OrderStatusEnum.FINISHED) {
      return 'success';
    }
    return order.isAwaitingPayment ? 'warning' : 'neutral';
  }

  toggleOrderExpand(orderId: string, event?: MouseEvent): void {
    if (event?.detail) {
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed && (event.currentTarget as HTMLElement).contains(selection.anchorNode)) {
        return;
      }
    }
    const expandedOrderIds = this.expandedOrderIds();
    if (expandedOrderIds.has(orderId)) {
      expandedOrderIds.delete(orderId);
    } else {
      expandedOrderIds.add(orderId);
    }
    this.expandedOrderIds.set(expandedOrderIds);
  }

  onSearch(query: string): void {
    this.searchInput.set(query);
    this.searchQuery.set(query);
    this.page.set(1);
  }

  goToPage(page: number): void {
    this.page.set(Math.min(Math.max(1, page), this.pagesCount()));
    window.scrollTo({ top: 0 });
  }

  /** Puts the order's products in the cart, keeping the larger quantity of any already there, and opens the cart */
  reorder(order: OrderDto): void {
    if (this.reorderingOrderId()) {
      return;
    }

    const cartItems: CartItemDto[] = order.items.map(item => ({
      productId: item.productId,
      qty: item.qty,
      additionalServiceIds: item.additionalServices?.map(service => service.id) ?? [],
    }));
    const analyticsData = order.items.map(item => ({
      sku: item.sku,
      name: item.name,
      price: item.price,
      qty: item.qty,
      source: 'reorder',
    }));

    this.reorderingOrderId.set(order.id);
    this.cartService.addCartItems(cartItems, analyticsData).pipe(
      finalize(() => this.reorderingOrderId.set(undefined)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: cart => {
        const orderedProductIds = new Set(order.items.map(item => item.productId));
        const unavailableCount = cart.populatedCartItems
          .filter(item => orderedProductIds.has(item.productId) && !item.isInStock)
          .length;
        this.dialog.open<CartModalComponent, CartModalData>(
          CartModalComponent,
          { data: { sourceForAnalytics: 'reorder' } },
        );
        if (unavailableCount) {
          this.languageService.getTranslation('account_orders.reorder_unavailable')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({ next: text => this.notyService.info(`${text} (${unavailableCount})`) });
        }
      },
      error: err => this.notyService.error(resolveHttpErrorMessage(err)),
    });
  }
}
